import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { THEMES } from '@/components/gallery/themes';
import { Lightbox } from '@/components/gallery/Lightbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GalleryViewer() {
  const { slug } = useParams();
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeAlbumId, setActiveAlbumId] = useState(null);
  const [lightboxState, setLightboxState] = useState({ open: false, media: [], index: 0 });

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/gc/galleries/${slug}`)
      .then((res) => {
        if (cancelled) return;
        setGallery(res.data);
        if (res.data?.albums?.length) setActiveAlbumId(res.data.albums[0].id);
      })
      .catch(() => setGallery(null))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-950">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!gallery) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-950 text-white">Gallery not found.</div>
    );
  }

  const activeAlbum = gallery.albums.find((a) => a.id === activeAlbumId) || gallery.albums[0];
  const themeName = (activeAlbum?.theme || gallery.theme || 'grid').toLowerCase();
  const ThemeComp = THEMES[themeName] || THEMES.grid;

  const openLightbox = (index) => {
    if (!gallery.lightbox_enabled) return;
    setLightboxState({ open: true, media: activeAlbum.media || [], index });
  };

  const accent = gallery.accent_color || '#d4af37';

  return (
    <div className="min-h-screen bg-stone-950 text-white" data-testid="gc-viewer" style={{ '--gc-accent': accent }}>
      <header className="max-w-6xl mx-auto px-6 py-10">
        <h1 className="text-4xl md:text-5xl font-bold font-display">{gallery.title}</h1>
        {gallery.description && (
          <p className="mt-3 text-white/80 max-w-3xl" dangerouslySetInnerHTML={{ __html: gallery.description }} />
        )}
      </header>

      {gallery.albums.length > 1 && (
        <div className="max-w-6xl mx-auto px-6 flex flex-wrap gap-2 mb-6">
          {gallery.albums.map((a) => (
            <button
              key={a.id}
              onClick={() => setActiveAlbumId(a.id)}
              className={`px-4 py-2 rounded-full text-sm font-semibold ${
                a.id === activeAlbumId
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-stone-900 text-stone-300 hover:bg-stone-800'
              }`}
            >
              {a.title}
              {gallery.show_counts && (
                <span className="ml-2 text-xs opacity-70">{a.media_count ?? (a.media || []).length}</span>
              )}
            </button>
          ))}
        </div>
      )}

      <div className="max-w-6xl mx-auto px-6 pb-20">
        {activeAlbum?.description && (
          <p className="text-white/70 mb-6" dangerouslySetInnerHTML={{ __html: activeAlbum.description }} />
        )}
        {activeAlbum && (activeAlbum.media || []).length ? (
          <ThemeComp
            media={activeAlbum.media}
            columns={gallery.columns || 4}
            gap={gallery.gap_px || 12}
            autoplay_ms={gallery.autoplay_ms || 0}
            onOpen={openLightbox}
            showCaptions={gallery.show_captions}
          />
        ) : (
          <div className="text-stone-500 text-sm italic">This album has no media yet.</div>
        )}
      </div>

      {lightboxState.open && (
        <Lightbox
          items={lightboxState.media}
          index={lightboxState.index}
          onClose={() => setLightboxState((s) => ({ ...s, open: false }))}
          showCaptions={gallery.show_captions}
        />
      )}
    </div>
  );
}
